import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import styles from './DashboardPage.module.css';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';

const isValidStripeCheckoutUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'checkout.stripe.com';
  } catch {
    return false;
  }
};

const isValidStripeOnboardingUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:' && parsed.hostname === 'connect.stripe.com';
  } catch {
    return false;
  }
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.events)) return payload.events;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const calcEventMetrics = (event) => {
  // サーバー側で集計した metrics を使用（dashboardRelations の hosted events に付与）
  if (event?.metrics) {
    return {
      sold:     toNumber(event.metrics.sold),
      revenue:  toNumber(event.metrics.revenue),
      capacity: toNumber(event.metrics.capacity),
      progress: toNumber(event.metrics.progress),
    };
  }
  // performing / providing イベントは統計なし
  return { sold: 0, revenue: 0, capacity: 0, progress: 0 };
};

const formatYen = (value) => `¥${toNumber(value).toLocaleString('ja-JP')}`;

const isCancelledStatus = (status) => {
  const value = String(status || 'none');
  return [
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ].includes(value);
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { t: tApi } = useTranslation('apiErrors');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sellerStatus, setSellerStatus] = useState(user?.seller_status || 'none');
  const [sellerActionLoading, setSellerActionLoading] = useState(false);
  const [showOnboardingIntro, setShowOnboardingIntro] = useState(false);
  const [cancelTargetEvent, setCancelTargetEvent] = useState(null);
  const [cancelEstimate, setCancelEstimate] = useState(null);
  const [cancelEstimateLoading, setCancelEstimateLoading] = useState(false);
  const [cancelEstimateError, setCancelEstimateError] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [abortingEventId, setAbortingEventId] = useState(null);
  const [abortTargetEvent, setAbortTargetEvent] = useState(null);
  const [draftEvents, setDraftEvents] = useState([]);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [deleteTargetDraft, setDeleteTargetDraft] = useState(null);

  const isSeller = ['organizer', 'artist', 'venue'].includes(user?.role);

  const loadSellerConnectStatus = async () => {
    if (!user?.id || !isSeller) return;

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/me/seller/connect-status`);
      setSellerStatus(res.data?.seller_status || 'none');
    } catch (e) {
      setSellerStatus(user?.seller_status || 'none');
    }
  };

  const loadDashboard = async () => {
    if (!user?.id || !isSeller) return;

    setLoading(true);
    setError('');

    try {
      await loadSellerConnectStatus();

      const relationsRes = await axios.get(`${process.env.REACT_APP_API_URL}/me/dashboard/relations`);

      const hosted = normalizeCollection(relationsRes.data?.hosted_events).map((event) => ({
        ...event,
        relationType: 'hosted',
      }));
      const performing = normalizeCollection(relationsRes.data?.performing_events).map((event) => ({
        ...event,
        relationType: 'performing',
      }));
      const providing = normalizeCollection(relationsRes.data?.providing_events).map((event) => ({
        ...event,
        relationType: 'providing',
      }));

      const merged = [...hosted, ...performing, ...providing].map((event) => ({
        ...event,
        isPast: (() => {
          const endRef = event?.end_date || event?.date;
          if (!endRef) return false;
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          return endRef < todayStr; // 終了日が今日より前のみ終了扱い（当日は開催中）
        })(),
        ...calcEventMetrics(event),
      }));

      setEvents(merged);

      const pendingTargets = hosted
        .filter((event) => !isCancelledStatus(event?.cancellation_status));
      const pendingResults = await Promise.all(
        pendingTargets.map(async (event) => {
          const eventPathId = event?.slug || event?.id;
          try {
            const res = await axios.get(
              `${process.env.REACT_APP_API_URL}/organizer/events/${eventPathId}/memories/pending`
            );
            const items = normalizeCollection(res.data);
            const pendingCount = items.filter((img) => String(img?.status || '') !== 'approved').length;
            return {
              eventId: event.id,
              eventPathId,
              eventName: event.name,
              total: pendingCount,
            };
          } catch (e) {
            return { eventId: event.id, eventPathId, eventName: event.name, total: 0 };
          }
        })
      );

      const total = pendingResults.reduce((sum, x) => sum + x.total, 0);
      const byEvent = pendingResults
        .filter((x) => x.total > 0)
        .sort((a, b) => b.total - a.total);

      setPendingEvents(byEvent);
      setPendingTotal(total);

      // 下書きイベント
      setDraftEvents(Array.isArray(relationsRes.data?.draft_events) ? relationsRes.data.draft_events : []);
    } catch (e) {
      setError(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSellerStatus(user?.seller_status || 'none');
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.seller_status]);

  useEffect(() => {
    if (sellerStatus !== 'none') {
      setShowOnboardingIntro(false);
    }
  }, [sellerStatus]);

  const ownedEvents = useMemo(
    () => events.filter((event) => event.relationType === 'hosted'),
    [events]
  );

  const performingEvents = useMemo(
    () => events.filter((event) => event.relationType === 'performing'),
    [events]
  );

  const providingEvents = useMemo(
    () => events.filter((event) => event.relationType === 'providing'),
    [events]
  );

  const relationSections = useMemo(() => {
    const sections = [];
    if (ownedEvents.length) {
      sections.push({ key: 'hosted', title: t('sectionHosted'), events: ownedEvents, relationType: 'hosted' });
    }
    if (performingEvents.length) {
      sections.push({ key: 'performing', title: t('sectionPerforming'), events: performingEvents, relationType: 'performing' });
    }
    if (providingEvents.length) {
      sections.push({ key: 'providing', title: t('sectionProviding'), events: providingEvents, relationType: 'providing' });
    }
    return sections;
  }, [ownedEvents, performingEvents, providingEvents, t]);

  const canViewSales = ownedEvents.length > 0;
  const canCreateEvent = sellerStatus === 'active';
  const canOpenOnboardingModal = sellerStatus === 'none' || sellerStatus === 'onboarding' || sellerStatus === 'restricted';

  const upcomingHostedCount = useMemo(
    () => ownedEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [ownedEvents]
  );
  const upcomingPerformingCount = useMemo(
    () => performingEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [performingEvents]
  );
  const upcomingProvidingCount = useMemo(
    () => providingEvents.filter((event) => !event.isPast && !isCancelledStatus(event?.cancellation_status)).length,
    [providingEvents]
  );

  const totalRevenue = useMemo(
    () => ownedEvents.reduce((sum, event) => sum + toNumber(event.revenue), 0),
    [ownedEvents]
  );

  const salesRows = useMemo(
    () =>
      ownedEvents.map((event) => ({
        id: event.id,
        name: event.name,
        sales: event.revenue,
        sold: event.sold,
      })),
    [ownedEvents]
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCancelEvent = async (event) => {
    setCancelTargetEvent(event);
    setCancelEstimate(null);
    setCancelEstimateError('');
    setCancelEstimateLoading(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/${event.id}/cancel-estimate`);
      setCancelEstimate(res.data || null);
    } catch (e) {
      setCancelEstimateError(t('cancelModal.estimateFailed'));
    } finally {
      setCancelEstimateLoading(false);
    }
  };

  const closeCancelModal = () => {
    if (cancelSubmitting) return;
    setCancelTargetEvent(null);
    setCancelEstimate(null);
    setCancelEstimateError('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetEvent?.id || cancelSubmitting) return;
    setCancelSubmitting(true);
    setCancelEstimateError('');

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${cancelTargetEvent.id}/cancel`);
      const payload = res.data || {};

      if (payload.requires_payment && payload.checkout_url) {
        if (!isValidStripeCheckoutUrl(payload.checkout_url)) {
          window.alert(t('cancelModal.invalidRedirect'));
          return;
        }
        window.location.href = payload.checkout_url;
        return;
      }

      window.alert(t('cancelModal.started'));
      setCancelTargetEvent(null);
      setCancelEstimate(null);
      setCancelEstimateError('');
      await loadDashboard();
    } catch (e) {
      setCancelEstimateError(getApiErrorMessage(e, tApi));
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleResumeCancelPayment = async (event) => {
    if (!event?.id) return;

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/events/${event.id}/cancel/resume`);
      const payload = res.data || {};

      if (payload.checkout_url) {
        if (!isValidStripeCheckoutUrl(payload.checkout_url)) {
          window.alert(t('cancelModal.invalidRedirect'));
          return;
        }
        window.location.href = payload.checkout_url;
        return;
      }

      window.alert(t('cancelModal.resumePaymentLinkFailed'));
    } catch (e) {
      window.alert(getApiErrorMessage(e, tApi));
    }
  };

  const handleAbortCancellation = (event) => {
    if (!event?.id || abortingEventId) return;
    setAbortTargetEvent(event);
  };

  const closeAbortModal = () => {
    if (abortingEventId) return;
    setAbortTargetEvent(null);
  };

  const handleConfirmAbortCancellation = async () => {
    if (!abortTargetEvent?.id || abortingEventId) return;

    setAbortingEventId(abortTargetEvent.id);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/events/${abortTargetEvent.id}/cancel/abort`);
      window.alert(t('abortModal.success'));
      setAbortTargetEvent(null);
      await loadDashboard();
    } catch (e) {
      window.alert(getApiErrorMessage(e, tApi));
    } finally {
      setAbortingEventId(null);
    }
  };

  const handleDeleteDraft = (draft) => {
    setDeleteTargetDraft(draft);
  };

  const closeDeleteDraftModal = () => {
    if (deletingDraftId) return;
    setDeleteTargetDraft(null);
  };

  const handleConfirmDeleteDraft = async () => {
    if (!deleteTargetDraft?.id || deletingDraftId) return;
    setDeletingDraftId(deleteTargetDraft.id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/me/events/${deleteTargetDraft.id}/draft`);
      setDraftEvents((prev) => prev.filter((d) => d.id !== deleteTargetDraft.id));
      setDeleteTargetDraft(null);
    } catch (e) {
      window.alert(getApiErrorMessage(e, tApi));
    } finally {
      setDeletingDraftId(null);
    }
  };

  const handleStartOnboarding = async () => {
    setSellerActionLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/me/seller/connect/start`, {
        accept_terms: true,
      });
      if (res.data?.onboarding_url) {
        if (!isValidStripeOnboardingUrl(res.data.onboarding_url)) {
          window.alert(t('onboarding.invalidRedirect'));
          return;
        }
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      window.alert(t('onboarding.startFailed'));
    } finally {
      setSellerActionLoading(false);
    }
  };

  const handleResumeOnboarding = async () => {
    setSellerActionLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/me/seller/connect/resume`);
      if (res.data?.onboarding_url) {
        if (!isValidStripeOnboardingUrl(res.data.onboarding_url)) {
          window.alert(t('onboarding.invalidRedirect'));
          return;
        }
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      window.alert(t('onboarding.resumeFailed'));
    } finally {
      setSellerActionLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.container}>{t('loading')}</div>;
  }

  if (error) {
    return <div className={styles.container}>{error}</div>;
  }

  const renderEventCard = (event, relationType) => {
    const eventPathId = event?.slug || event?.id;
    const isHost = relationType === 'hosted';
    const canManageEvent = isHost && !event.isPast;
    const isCanceledEvent = isCancelledStatus(event?.cancellation_status);
    const isCancellationPendingPayment = (event?.cancellation_status || '') === 'cancellation_pending_payment';
    const canStartCancellation = canManageEvent && !isCanceledEvent && !isCancellationPendingPayment;
    const canResumeCancellationPayment = isHost && isCancellationPendingPayment;
    const canViewEventSales = isHost;
    const showSalesOnlyActions = isCanceledEvent;
    const flyerUrl = event?.flyers?.[0]?.url || '';

    return (
      <article key={`${relationType}-${event.id}`} className={styles.eventCard}>
        <img className={styles.thumb} src={flyerUrl} alt="flyer" />
        <div>
          <h3 className={styles.eventName}>
            {event.name}
            {relationType !== 'hosted' && (
              <span className={styles.relationTag}>
                {relationType === 'performing' ? t('tagPerforming') : t('tagProviding')}
              </span>
            )}
          </h3>
          <p className={styles.eventDate}>{String(event.date || '').slice(0, 10)}</p>
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} style={{ width: `${event.progress}%` }} />
          </div>
          <div className={styles.metricsRow}>
            {isHost ? (
              <>
                <span>{t('soldCount')} {event.sold}{event.capacity > 0 ? ` / ${event.capacity}` : ''}</span>
                <span>{t('revenue')} {formatYen(event.revenue)}</span>
              </>
            ) : (
              <span className={styles.muted}>{t('salesHostOnly')}</span>
            )}
          </div>
          <div className={styles.actions}>
            {showSalesOnlyActions ? (
              canViewEventSales ? (
                <Link className={`${styles.btn} ${styles.salesBtn}`} to={`/events/${eventPathId}/sales`}>
                  <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                  {t('btnSales')}
                </Link>
              ) : null
            ) : (
              <>
                {canStartCancellation && (
                  <>
                    <Link className={`${styles.btn} ${styles.editBtn}`} to={`/events/${eventPathId}/edit`}>
                      <img className={styles.actionIcon} src="/icons/edit.svg" alt="" aria-hidden="true" />
                      {t('btnEdit')}
                    </Link>
                    <button className={`${styles.btn} ${styles.btnDanger} ${styles.cancelBtn}`} onClick={() => handleCancelEvent(event)}>
                      <img className={styles.actionIcon} src="/icons/cancelled.svg" alt="" aria-hidden="true" />
                      {t('btnCancel')}
                    </button>
                  </>
                )}
                {canResumeCancellationPayment && (
                  <>
                    <button className={`${styles.btn} ${styles.resumeBtn}`} onClick={() => handleResumeCancelPayment(event)}>
                      <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                      {t('btnResumeCancellation')}
                    </button>
                    <button
                      className={`${styles.btn} ${styles.abortBtn}`}
                      onClick={() => handleAbortCancellation(event)}
                      disabled={abortingEventId === event.id}
                    >
                      {abortingEventId === event.id ? t('btnAborting') : t('btnAbortCancellation')}
                    </button>
                  </>
                )}
                {canViewEventSales && (
                  <Link className={`${styles.btn} ${styles.salesBtn}`} to={`/events/${eventPathId}/sales`}>
                    <img className={styles.actionIcon} src="/icons/sales.svg" alt="" aria-hidden="true" />
                    {t('btnSales')}
                  </Link>
                )}
                <Link className={styles.btn} to={`/events/${eventPathId}`}>{t('btnDetail')}</Link>
              </>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Link to="/" className={styles.backLink}>{t('backToSite')}</Link>

          <div className={styles.accountWrap}>
            <button
              type="button"
              className={styles.accountButton}
              onClick={() => setMenuOpen((prev) => !prev)}
            >≡</button>
            {menuOpen && (
              <div className={styles.accountMenu}>
                <Link className={styles.menuItem} to={`/profile/${user?.slug || 'my'}`} onClick={() => setMenuOpen(false)}>
                  {t('menuProfile')}
                </Link>
                <Link
                  className={styles.menuItem}
                  to="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                >
                  {t('menuLogout')}
                </Link>
              </div>
            )}
          </div>
        </header>

        {canOpenOnboardingModal && showOnboardingIntro && (
          <div
            className={styles.onboardingModalBackdrop}
            onClick={() => {
              if (!sellerActionLoading) setShowOnboardingIntro(false);
            }}
          >
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('onboarding.title')}>
              <h3 className={styles.onboardingIntroTitle}>{t('onboarding.title')}</h3>
              <p className={styles.onboardingIntroLead}>
                {t('onboarding.lead')}
              </p>
              <div className={styles.onboardingProfileNotice} style={{marginBottom: '1em'}}>
                {t('onboarding.profileNotice').split('\n').map((line, i) => (
                  <p key={i} style={{margin: '4px 0'}}>{line}</p>
                ))}
              </div>
              <ul className={styles.onboardingIntroList}>
                <li><img src="/icons/clock.svg" alt={t('onboarding.clockAlt')} style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />{t('onboarding.point1')}</li>
                <li><img src="/icons/camera.svg" alt={t('onboarding.cameraAlt')} style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />{t('onboarding.point2')}</li>
                <li><img src="/icons/ginko.svg" alt={t('onboarding.bankAlt')} style={{width: '1em', verticalAlign: 'middle', marginRight: '0.3em'}} />{t('onboarding.point3')}</li>
              </ul>
              <p className={styles.onboardingIntroNote}>
                <Link className={styles.onboardingIntroLink} to="/seller-kyc-guide">
                  {t('onboarding.guideLink')}
                </Link>
              </p>
              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={sellerStatus === 'none' ? handleStartOnboarding : handleResumeOnboarding}
                  disabled={sellerActionLoading}
                >
                  {sellerActionLoading ? t('processing') : sellerStatus === 'none' ? t('onboarding.start') : t('onboarding.resume')}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={() => setShowOnboardingIntro(false)}
                  disabled={sellerActionLoading}
                >
                  {t('onboarding.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {cancelTargetEvent && (
          <div className={styles.onboardingModalBackdrop} onClick={closeCancelModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('cancelModal.title')}>
              <h3 className={styles.onboardingIntroTitle}>{t('cancelModal.title')}</h3>
              <p className={styles.onboardingIntroLead}>{t('cancelModal.lead', { name: cancelTargetEvent.name })}</p>

              {cancelEstimateLoading ? (
                <p className={styles.muted}>{t('cancelModal.estimateLoading')}</p>
              ) : (
                <>
                  <ul className={styles.cancelEstimateList}>
                    <li>{t('cancelModal.refundReservations')}{toNumber(cancelEstimate?.refund_target_reservation_count || cancelEstimate?.refund_target_reservation_count === 0 ? cancelEstimate?.refund_target_reservation_count : cancelEstimate?.refund_target_count)}{t('unitEvents')}</li>
                    <li>{t('cancelModal.refundDonations')}{toNumber(cancelEstimate?.refund_target_donation_count)}{t('unitEvents')}</li>
                    <li>{t('cancelModal.refundTotalTickets')} {formatYen(cancelEstimate?.refund_total_amount_reservations)}</li>
                    <li>{t('cancelModal.refundTotalDonations')} {formatYen(cancelEstimate?.refund_total_amount_donations)}</li>
                    <li>{t('cancelModal.stripeFeeTickets')} {formatYen(cancelEstimate?.estimated_stripe_fee_reservations)}</li>
                    <li>{t('cancelModal.stripeFeeDonations')} {formatYen(cancelEstimate?.donation_processor_fee_total)}</li>
                    <li>{t('cancelModal.chargebackFee')}{formatYen(cancelEstimate?.estimated_chargeback_fee_total)}</li>
                    <li>{t('cancelModal.feeTotal')} {formatYen(cancelEstimate?.organizer_burden_estimate)}</li>
                    <li><strong>{t('cancelModal.cancelFee')} {formatYen(cancelEstimate?.estimated_cancel_fee_gross)}</strong></li>
                  </ul>
                  <p className={styles.cancelWarningText}>{t('cancelModal.warning')}</p>
                </>
              )}

              {cancelEstimateError && <p className={styles.connectError}>{cancelEstimateError}</p>}

              <div className={styles.onboardingIntroActions}>
                <button type="button" className={styles.connectPrimaryBtn} onClick={handleConfirmCancel} disabled={cancelEstimateLoading || cancelSubmitting || !!cancelEstimateError || !cancelEstimate}>
                  {cancelSubmitting ? t('cancelModal.processing') : t('cancelModal.confirm')}
                </button>
                <button type="button" className={styles.onboardingCancelBtn} onClick={closeCancelModal} disabled={cancelSubmitting}>
                  {t('cancelModal.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {abortTargetEvent && (
          <div className={styles.onboardingModalBackdrop} onClick={closeAbortModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('abortModal.title')}>
              <h3 className={styles.onboardingIntroTitle}>{t('abortModal.title')}</h3>
              <p className={styles.onboardingIntroLead}>{t('abortModal.lead', { name: abortTargetEvent.name })}</p>
              <p className={styles.cancelWarningText}>{t('abortModal.warning')}</p>

              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={handleConfirmAbortCancellation}
                  disabled={abortingEventId === abortTargetEvent.id}
                >
                  {abortingEventId === abortTargetEvent.id ? t('abortModal.aborting') : t('abortModal.confirm')}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={closeAbortModal}
                  disabled={abortingEventId === abortTargetEvent.id}
                >
                  {t('abortModal.back')}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteTargetDraft && (
          <div className={styles.onboardingModalBackdrop} onClick={closeDeleteDraftModal}>
            <div className={styles.onboardingModal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('draftDeleteTitle')}>
              <h3 className={styles.onboardingIntroTitle}>{t('draftDeleteTitle')}</h3>
              <p className={styles.onboardingIntroLead}>{t('draftDeleteLead', { name: deleteTargetDraft.name || t('draftUntitled') })}</p>
              <p className={styles.cancelWarningText}>{t('draftDeleteWarning')}</p>
              <div className={styles.onboardingIntroActions}>
                <button
                  type="button"
                  className={`${styles.connectPrimaryBtn} ${styles.btnDanger}`}
                  onClick={handleConfirmDeleteDraft}
                  disabled={deletingDraftId === deleteTargetDraft.id}
                >
                  {deletingDraftId === deleteTargetDraft.id ? t('draftDeleting') : t('draftDeleteBtn')}
                </button>
                <button
                  type="button"
                  className={styles.onboardingCancelBtn}
                  onClick={closeDeleteDraftModal}
                  disabled={deletingDraftId === deleteTargetDraft.id}
                >
                  {t('abortModal.back')}
                </button>
              </div>
            </div>
          </div>
        )}

        <section className={styles.section}>
          <div className={styles.summaryGrid}>
            {upcomingHostedCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  {t('summaryHosted')}
                </div>
                <div className={styles.summaryValue}>{upcomingHostedCount}{t('unitEvents')}</div>
              </div>
            )}
            {upcomingPerformingCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  {t('summaryPerforming')}
                </div>
                <div className={styles.summaryValue}>{upcomingPerformingCount}{t('unitEvents')}</div>
              </div>
            )}
            {upcomingProvidingCount > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/calender.svg" alt="" aria-hidden="true" />
                  {t('summaryProviding')}
                </div>
                <div className={styles.summaryValue}>{upcomingProvidingCount}{t('unitEvents')}</div>
              </div>
            )}
            {pendingTotal > 0 && (
              <div className={styles.summaryCard}>
                <div className={`${styles.summaryLabel} ${styles.summaryLabelWithIcon}`}>
                  <img className={styles.summaryIcon} src="/icons/image.svg" alt="" aria-hidden="true" />
                  <span>{t('summaryGallery')}</span>
                </div>
                <div className={styles.summaryValue}>{pendingTotal}{t('unitEvents')}</div>
              </div>
            )}
          </div>
        </section>

        {/* 下書きエリア */}
        {draftEvents.length > 0 && (
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{t('sectionDraft')}</h2>
            </div>
            <div className={styles.eventList}>
              {draftEvents.map((draft) => (
                <article key={draft.id} className={styles.eventCard}>
                  <div
                    className={styles.thumb}
                    style={{ background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}
                  >
                    {t('sectionDraft')}
                  </div>
                  <div>
                    <h3 className={styles.eventName}>{draft.name || t('draftUntitled')}</h3>
                    <p className={styles.eventDate}>
                      {t('draftUpdatedAt')} {draft.updated_at ? new Date(draft.updated_at).toLocaleDateString() : '—'}
                    </p>
                    <div className={styles.actions}>
                      <Link
                        className={`${styles.btn} ${styles.editBtn}`}
                        to={`/events/create?draft=${draft.id}`}
                      >
                        {t('draftResume')}
                      </Link>
                      <a
                        className={styles.btn}
                        href={`/events/${draft.id}?preview=1`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('draftPreview')}
                      </a>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnDanger}`}
                        onClick={() => handleDeleteDraft(draft)}
                      >
                        {t('draftDelete')}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>{t('sectionEvents')}</h2>
            {canCreateEvent ? (
              <Link className={styles.createButton} to="/events/create">{t('createEvent')}</Link>
            ) : (
              <div className={styles.createHintBlock}>
                <button
                  type="button"
                  className={styles.connectPrimaryBtn}
                  onClick={() => setShowOnboardingIntro(true)}
                  disabled={sellerActionLoading}
                >
                  {sellerActionLoading ? t('processing') : sellerStatus === 'none' ? t('startSelling') : t('continueOnboarding')}
                </button>
              </div>
            )}
          </div>
          {relationSections.map((section) => (
            <div key={section.key} className={styles.relationBlock}>
              <h3 className={styles.relationTitle}>{section.title}</h3>
              {(() => {
                const canceledEvents = section.events.filter((event) => isCancelledStatus(event?.cancellation_status));
                const activeEvents = section.events.filter((event) => !isCancelledStatus(event?.cancellation_status));
                const upcomingEvents = activeEvents.filter((event) => !event.isPast);
                const endedEvents = activeEvents.filter((event) => event.isPast);

                return (
                  <>
                    {upcomingEvents.length > 0 ? (
                      <div className={styles.eventList}>
                        {upcomingEvents.map((event) => renderEventCard(event, section.relationType))}
                      </div>
                    ) : (
                      <p className={styles.emptyText}>{t('upcomingEmpty')}</p>
                    )}

                    {endedEvents.length > 0 && (
                      <details className={styles.fold}>
                        <summary>{t('endedEvents', { count: endedEvents.length })}</summary>
                        <div className={styles.eventList}>
                          {endedEvents.map((event) => renderEventCard(event, section.relationType))}
                        </div>
                      </details>
                    )}

                    {canceledEvents.length > 0 && (
                      <details className={styles.fold}>
                        <summary>{t('canceledEvents', { count: canceledEvents.length })}</summary>
                        <div className={styles.eventList}>
                          {canceledEvents.map((event) => renderEventCard(event, section.relationType))}
                        </div>
                      </details>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
          {!relationSections.length && <p className={styles.emptyText}>{t('eventsEmpty')}</p>}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>{t('sectionGallery')}</h2>
            <Link className={styles.allLink} to="/gallery-admin">
              {t('galleryAdminLink')}
            </Link>
          </div>
          <div className={`${styles.gallerySection} ${pendingTotal > 0 ? styles.galleryAlert : ''}`}>
            {pendingTotal === 0 ? (
              <p className={styles.muted}>{t('galleryEmpty')}</p>
            ) : (
              <>
                <p>{t('galleryPending', { count: pendingTotal })}</p>
                {pendingEvents.map((item) => (
                  <div key={item.eventId}>
                    <Link className={styles.allLink} to={`/events/${item.eventPathId}/gallery-admin`}>
                      {t('galleryEventLink', { name: item.eventName, count: item.total })}
                    </Link>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
